/**
 * The agents' cursors on the stage — where each one is working and what it is doing, the way
 * collaborators' pointers show in a shared canvas. Every tool run passes through here, from
 * the chat, the bridge or the local agent alike; subagents working side by side each get
 * their own pointer, and the stage draws them all — the demo presence where there is one, the
 * overlay's pills otherwise. A subagent's pointer lives with its loop (`open`/`close`): it works
 * while the loop runs, rests where it worked once the loop is through, and goes with the answer.
 */
import type { Vec2, Level } from '../core/hp/types';
import { boundsOf } from '../core/geometry/footprint';
import { interiorPoint } from '../state/placement';
import { TOOL_CURSOR_KIND, TOOL_PROGRESS, type CursorKind } from './toolLabels';
import { aliases } from './aliases';
import { SIDE_FROM_INPUT, wallBySide } from './layout';
import { t } from '../i18n/I18n';
import { AGENT_LINGER_MS, roleById } from './roles';

export interface CursorState {
  visible: boolean;
  /** A roster role id, or the name of an agent outside the roster (the bridge, the local agent). */
  key: string;
  /** The role's name in the interface language, else the key. */
  name: string;
  label: string;
  kind: CursorKind;
  /** Plan point the cursor stands at; null parks it by the dock. */
  point: Vec2 | null;
  /** Bring the point into view if it is off screen (a move or a new item, not a read). */
  reveal: boolean;
  /** At work: the pulse under the arrow. */
  working: boolean;
  item?: string;
  hold?: boolean;
  updatedAt: number;
}

/** A cursor before it has moved; the name is the interface's word for an agent, read when the cursor is made. */
const idle = (key: string): CursorState => ({ visible: false, key, name: roleById(key)?.name ?? key, label: '', kind: 'other', point: null, reveal: false, working: false, updatedAt: 0 });
const num = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Where a call is aimed, from its arguments alone — before the tool runs. */
export function cursorTargetFor(name: string, input: unknown, level: Level | undefined): Vec2 | null {
  return cursorAimFor(name, input, level).point;
}

/** Where a call is aimed and the item it works on, from its arguments alone. */
export function cursorAimFor(name: string, input: unknown, level: Level | undefined): { point: Vec2 | null; item?: string } {
  const point = aimPoint(name, input, level), args = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const itemRef = typeof args.item_id === 'string' ? args.item_id : typeof args.anchor === 'string' ? args.anchor : typeof args.table === 'string' ? args.table : typeof args.id === 'string' && /item/.test(name) ? args.id : undefined;
  const wanted = itemRef && level ? aliases.resolve('item', itemRef) : undefined;
  const item = level?.items.find(candidate => candidate.id === wanted)?.id;
  return item ? { point, item } : { point };
}

function aimPoint(name: string, input: unknown, level: Level | undefined): Vec2 | null {
  const args = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  if (!level) return null;
  const x = num(args.x), z = num(args.z);
  if (name === 'add_room') { const width = num(args.width) ?? 0, depth = num(args.depth) ?? 0; return x !== undefined && z !== undefined ? { x: x + width / 2, z: z + depth / 2 } : null; }
  if (name === 'add_wall') { const x1 = num(args.x1), z1 = num(args.z1), x2 = num(args.x2), z2 = num(args.z2); return [x1, z1, x2, z2].every(v => v !== undefined) ? { x: (x1! + x2!) / 2, z: (z1! + z2!) / 2 } : null; }
  const roomIndex = num(args.room_index) ?? num(args.index) ?? num(args.room);
  const room = roomIndex !== undefined ? level.rooms.find(candidate => candidate.index === roomIndex) : undefined;
  const wallRef = typeof args.wall_id === 'string' ? args.wall_id : typeof args.wall === 'string' ? args.wall : name === 'update_wall' && typeof args.id === 'string' ? args.id : undefined;
  const side = typeof args.side === 'string' ? SIDE_FROM_INPUT[args.side.toLowerCase()] : undefined;
  const wallId = wallRef ? aliases.resolve('wall', wallRef) : room && side ? wallBySide(level, room, side)?.id : undefined;
  if (wallId) {
    const wall = level.walls.find(candidate => candidate.id === wallId);
    if (wall) {
      const along = num(args.offset) ?? num(args.wall_offset);
      return along !== undefined ? { x: wall.start.x + wall.dir.x * along, z: wall.start.z + wall.dir.z * along } : wall.center;
    }
  }
  if (name === 'update_opening' && typeof args.id === 'string') {
    const opening = level.openings.find(candidate => candidate.id === aliases.resolve('opening', String(args.id)));
    const wall = opening && level.walls.find(candidate => candidate.id === opening.wallId);
    if (opening && wall) return { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
  }
  const itemRef = typeof args.item_id === 'string' ? args.item_id : typeof args.anchor === 'string' ? args.anchor : typeof args.table === 'string' ? args.table : typeof args.id === 'string' && /item/.test(name) ? args.id : undefined;
  if (itemRef) { const item = level.items.find(candidate => candidate.id === aliases.resolve('item', itemRef)); if (item) return item.position; }
  if (name === 'delete' && Array.isArray(args.ids) && args.kind === 'item') { const item = level.items.find(candidate => candidate.id === aliases.resolve('item', String(args.ids![0 as never]))); if (item) return item.position; }
  if (x !== undefined && z !== undefined) return { x, z };
  if (room && room.polygon.length >= 3 && ['add_item', 'update_room', 'set_material', 'focus', 'select', 'get_scene', 'furnish_room', 'set_room_finish', 'place_along_wall', 'add_opening'].includes(name)) return interiorPoint(room.polygon);
  return null;
}

/** The middle of the floor's walls: where a subagent's pointer waits before its first tool. */
function levelCentre(level: Level | undefined): Vec2 | null {
  const points = level?.walls.flatMap(wall => [wall.start, wall.end]) ?? [];
  if (!points.length) return null;
  const box = boundsOf(points);
  return { x: (box.minX + box.maxX) / 2, z: (box.minZ + box.maxZ) / 2 };
}

type Listener = () => void;
const ACTIVE_KINDS: ReadonlySet<CursorKind> = new Set(['draw', 'place', 'paint']);
/** No two visible plates ever stand closer than CROWDED, whatever the order roles appear, move or rest in: the mover steps down the plan. */
const CROWDED = .4, STEP = .55;

export class AgentCursor {
  private cursors = new Map<string, CursorState>();
  private listeners = new Set<Listener>();
  private hideTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Keys whose subagent loop is running: a finished tool leaves their pointer on the stage. */
  private loops = new Set<string>();
  /** Null until a run names its agent: the interface's word for an agent then. */
  private named: string | null = null;
  private visibleList: CursorState[] = [];
  private latest: CursorState | null = null;

  private get agentName(): string { return this.named ?? t('#AgentCursorName'); }
  /** The cursor that moved last — the one place a single-pointer reader looks. */
  get = (): CursorState => this.latest ??= idle(this.agentName);
  /** Every pointer on the stage right now, in the order they appeared. */
  all = (): CursorState[] => this.visibleList;
  subscribe = (fn: Listener): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };

  private set(key: string, patch: Partial<CursorState>): void {
    const current = this.cursors.get(key) ?? idle(key);
    const next = { ...current, ...patch, key, name: roleById(key)?.name ?? key, updatedAt: Date.now() };
    if (next.visible && next.point) next.point = this.apart(key, next.point);
    this.cursors.set(key, next);
    this.latest = next;
    this.visibleList = [...this.cursors.values()].filter(cursor => cursor.visible);
    for (const fn of this.listeners) fn();
  }

  /** The first slot down the plan from the point that stands clear of where every other visible plate is now. */
  private apart(key: string, point: Vec2): Vec2 {
    const taken = [...this.cursors.values()].flatMap(cursor => cursor.key !== key && cursor.visible && cursor.point ? [cursor.point] : []);
    for (let below = 0; ; below++) {
      const slot = below ? { x: point.x, z: point.z + below * STEP } : point;
      if (taken.every(other => Math.hypot(other.x - slot.x, other.z - slot.z) >= CROWDED)) return slot;
    }
  }

  private keep(key: string): void {
    const timer = this.hideTimers.get(key);
    if (timer) { clearTimeout(timer); this.hideTimers.delete(key); }
  }

  /** Whose cursor a call without a name belongs to; the label reads "Дизайнер · ставит предмет". */
  setAgent(name: string): void {
    if (name === this.agentName) return;
    const previous = this.agentName;
    this.named = name;
    const cursor = this.cursors.get(previous);
    if (cursor?.visible) { this.hideOne(previous); this.set(name, { ...cursor }); }
  }

  /** A subagent's loop starts: its pointer is on the stage at work, where it was or in the middle of the floor. */
  open(key: string, level?: Level): void {
    this.keep(key);
    this.loops.add(key);
    const previous = this.cursors.get(key);
    this.set(key, { visible: true, working: true, label: t('#AgentPanelSubagentWorking'), kind: 'other', reveal: false,
      point: previous?.visible && previous.point ? previous.point : levelCentre(level) });
  }

  /** The loop is through: the pulse stops, the pointer rests where it worked until the answer ends. */
  close(key: string): void {
    this.loops.delete(key);
    if (this.cursors.get(key)?.visible) this.set(key, { working: false, hold: false });
  }

  /** The simulation's step: the pointer goes to the work and pulses. */
  work(key: string, task: { point: Vec2 | null; item?: string; label: string; hold?: boolean }): void {
    this.keep(key);
    const previous = this.cursors.get(key);
    this.set(key, { visible: true, working: true, kind: 'other', reveal: false, label: task.label, item: task.item, hold: task.hold,
      point: task.point ?? (previous?.visible ? previous.point : null) });
  }

  /** The simulation's agent is through: it rests on its last work. */
  rest(key: string): void { if (this.cursors.get(key)?.visible) this.set(key, { working: false, hold: false }); }

  begin(name: string, input: unknown, level: Level | undefined, key = this.agentName): void {
    this.keep(key);
    const aim = cursorAimFor(name, input, level);
    const kind = TOOL_CURSOR_KIND[name] ?? 'other';
    const previous = this.cursors.get(key);
    this.set(key, { visible: true, label: TOOL_PROGRESS[name] ?? name, kind, item: aim.item, point: aim.point ?? (kind === 'read' || this.loops.has(key) ? previous?.point ?? null : null),
      reveal: ACTIVE_KINDS.has(kind), working: true });
  }

  finish(name: string, summary: string, anchor: Vec2 | null | undefined, ok: boolean, key = this.agentName): void {
    const previous = this.cursors.get(key), open = this.loops.has(key);
    this.set(key, { label: ok ? summary : t('#AgentCursorFailed', TOOL_PROGRESS[name]?.toLowerCase() ?? name), point: anchor ?? previous?.point ?? null, working: open,
      reveal: !!anchor && ACTIVE_KINDS.has(TOOL_CURSOR_KIND[name] ?? 'other') });
    if (!open) this.hideLater(key);
  }

  /** The answer is over: every cursor lingers as long as the bots do, then goes. */
  done(ms = AGENT_LINGER_MS): void {
    this.loops.clear();
    for (const key of this.cursors.keys()) this.hideLater(key, ms);
  }

  /** Stop: the stage is cleared at once. */
  hide(): void {
    this.loops.clear();
    for (const key of [...this.cursors.keys()]) this.hideOne(key);
  }

  private hideOne(key: string): void {
    this.keep(key);
    if (this.cursors.get(key)?.visible) this.set(key, { visible: false, working: false, hold: false });
  }

  private hideLater(key: string, ms = 2800): void {
    this.keep(key);
    this.hideTimers.set(key, setTimeout(() => { this.hideTimers.delete(key); this.hideOne(key); }, ms));
  }
}

export const agentCursor = new AgentCursor();
